/**
 * Mapeamento de campos técnicos para labels humanas
 * Usado para exibir dados de importação de forma legível
 */

const EMPTY_VALUE = '—';

// Campos técnicos que devem ser ocultados da UI
const HIDDEN_FIELDS = new Set([
  'rawData',
  'normalizedData',
  'rowHash',
  'validationErrors',
  'importHistoryId',
  'createdAt',
  'updatedAt',
  'id',
  'importId',
  '__companyIdResolved',
]);

const PREVIEW_EXCLUDED_COLUMNS = new Set(['Linha', 'Status', 'Status da Linha']);

const LINE_NUMBER_KEYS = [
  '_rowNumber',
  'rowNumber',
  'row_number',
  'linha',
  'line',
  'lineNumber',
];

const ROW_STATUS_KEYS = [
  'rowStatus',
  'row_status',
  'validationStatus',
  'validation_status',
  'statusLinha',
  'status_linha',
  'status',
  'resultStatus',
  'result_status',
];

const PREVIEW_PRIORITY = [
  'Operação',
  'Empresa',
  'Nome Completo',
  'CPF',
  'Setor',
  'Cargo',
  'Quantidade de Pessoas',
  'Quantidade',
  'Descrição das Atividades',
  'Jornada de Trabalho',
  'Turno',
];

// Ordem oficial/prioritária das colunas exibidas na UI
export const COLUMN_ORDER: string[] = [
  'Linha',
  'Operação',
  'Empresa',
  'Nome Completo',
  'CPF',
  'Setor',
  'Cargo',
  'Quantidade de Pessoas',
  'Quantidade',
  'Descrição das Atividades',
  'Conteúdo da Tarefa',
  'Média Hora Extra 3 meses por dia',
  'Jornada de Trabalho',
  'Tempo de Refeição',
  'Modo Operacional',
  'Exigência de Tempo',
  'Ritmo de Trabalho',
  'Volume de Produção',
  'Pausas',
  'Ginástica Laboral',
  'Medidas Preventivas',
  'Comprovação das Medidas Preventivas',
  'Status da Linha',
];

// Mapeamento de nomes de campo para labels humanas
export const FIELD_LABELS: Record<string, string> = {
  // Estrutura Organizacional / NR1
  setor: 'Setor',
  cargo: 'Cargo',
  quantidade: 'Quantidade de Pessoas',
  quantidade_de_colaboradores: 'Quantidade de Pessoas',
  numero_pessoas: 'Quantidade de Pessoas',
  headcount: 'Quantidade de Pessoas',
  jornada: 'Jornada de Trabalho',
  jornada_de_trabalho: 'Jornada de Trabalho',
  workinghours: 'Jornada de Trabalho',
  // PJC-70: média diária de hora extra (fonte da Probabilidade)
  horaExtra: 'Média Hora Extra 3 meses por dia',
  hora_extra: 'Média Hora Extra 3 meses por dia',
  overtimehours: 'Média Hora Extra 3 meses por dia',
  overtime_hours: 'Média Hora Extra 3 meses por dia',
  // V57: coluna Rodízio (nunca foi adicionada aqui, caía na chave crua)
  rodizio: 'Rodízio',
  rotation: 'Rodízio',
  ritmo_trabalho: 'Ritmo de Trabalho',
  conteudo_tarefa: 'Conteúdo da Tarefa',
  conteudotarefas: 'Conteúdo da Tarefa',
  medidas_preventivas: 'Medidas Preventivas',
  comprovacao_medidas: 'Comprovação das Medidas Preventivas',
  comprovacaomedidas: 'Comprovação das Medidas Preventivas',
  descricao: 'Descrição das Atividades',
  descricao_atividades: 'Descrição das Atividades',
  descricao_das_atividades: 'Descrição das Atividades',
  jobdescription: 'Descrição das Atividades',
  refeicao: 'Tempo de Refeição',
  modoopertatorio: 'Modo Operacional',
  modooperatorio: 'Modo Operacional',
  exigenciatempo: 'Exigência de Tempo',
  volumproducao: 'Volume de Produção',
  volumeproducao: 'Volume de Produção',
  pausas: 'Pausas',
  ginasticalaboral: 'Ginástica Laboral',
  responsavel: 'Responsável',
  email: 'Email',
  telefone: 'Telefone',
  observacoes: 'Observações',

  // Base de Vidas / aliases comuns
  operacao: 'Operação',
  operation: 'Operação',
  operationtype: 'Operação',
  operation_type: 'Operação',
  action: 'Operação',
  empresa: 'Empresa',
  company: 'Empresa',
  companyname: 'Empresa',
  company_name: 'Empresa',
  nome_empresa: 'Empresa',
  nomecompleto: 'Nome Completo',
  nome_completo: 'Nome Completo',
  fullname: 'Nome Completo',
  full_name: 'Nome Completo',
  employee_name: 'Nome Completo',
  employeename: 'Nome Completo',
  employee_full_name: 'Nome Completo',
  cpf: 'CPF',
  cpf_colaborador: 'CPF',
  employeecpf: 'CPF',
  employee_cpf: 'CPF',
  document: 'Documento',
  documento: 'Documento',

  // Variações comuns
  sector: 'Setor',
  position: 'Cargo',
  job_title: 'Cargo',
  jobtitle: 'Cargo',
  department: 'Departamento',
  team: 'Equipe',
  count: 'Quantidade',
  quantity: 'Quantidade de Pessoas',
  number_of_people: 'Quantidade de Pessoas',
  working_hours: 'Jornada de Trabalho',
  shift: 'Turno',
  work_rhythm: 'Ritmo de Trabalho',
  task_content: 'Conteúdo da Tarefa',
  preventive_measures: 'Medidas Preventivas',
  manager: 'Responsável',
  notes: 'Observações',
  comments: 'Comentários',
  rowstatus: 'Status da Linha',
  row_status: 'Status da Linha',
  validationstatus: 'Status da Linha',
  validation_status: 'Status da Linha',
};

function getSourceData(data: Record<string, any>): Record<string, any> {
  if (!data || typeof data !== 'object') {
    return {};
  }

  if (data.normalizedData && typeof data.normalizedData === 'object') {
    return data.normalizedData;
  }

  return data;
}

function getCandidateSources(data: Record<string, any>): Record<string, any>[] {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const sources: Record<string, any>[] = [data];

  if (data.normalizedData && typeof data.normalizedData === 'object') {
    sources.push(data.normalizedData);
  }

  if (data.rawData && typeof data.rawData === 'object') {
    sources.push(data.rawData);
  }

  return sources;
}

/**
 * Filtra e mapeia campos para exibição
 * Remove campos técnicos e aplica labels humanas
 */
export function getDisplayFields(data: Record<string, any>): Record<string, string> {
  if (!data || typeof data !== 'object') {
    return {};
  }

  const displayData: Record<string, string> = {};
  const sourceData = getSourceData(data);

  for (const [key, value] of Object.entries(sourceData)) {
    if (HIDDEN_FIELDS.has(key)) {
      continue;
    }

    if (value === null || value === undefined || value === '') {
      continue;
    }

    if (typeof value === 'object' && !(value instanceof Date)) {
      continue;
    }

    const label = FIELD_LABELS[key.toLowerCase()] || key;
    displayData[label] = formatFieldValue(value);
  }

  return displayData;
}

/**
 * Formata valor de campo para exibição
 */
function formatFieldValue(value: any): string {
  if (value === null || value === undefined || value === '') {
    return EMPTY_VALUE;
  }

  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }

  if (value instanceof Date) {
    return value.toLocaleDateString('pt-BR');
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '[object Object]';
    }
  }

  const str = String(value).trim();
  if (!str) {
    return EMPTY_VALUE;
  }

  return str.length > 100 ? `${str.substring(0, 100)}...` : str;
}

function getRawFieldValue(obj: Record<string, any>, possibleKeys: string[]): any {
  if (!obj || typeof obj !== 'object') {
    return null;
  }

  const entries = Object.entries(obj);

  for (const key of possibleKeys) {
    if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
      return obj[key];
    }

    const foundEntry = entries.find(([entryKey, entryValue]) => {
      return entryKey.toLowerCase() === key.toLowerCase() && entryValue !== null && entryValue !== undefined && entryValue !== '';
    });

    if (foundEntry) {
      return foundEntry[1];
    }
  }

  return null;
}

function getFieldValueFromSources(sources: Record<string, any>[], possibleKeys: string[]): any {
  for (const source of sources) {
    const value = getRawFieldValue(source, possibleKeys);
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }

  return null;
}

/**
 * Obtém lista de colunas para exibição em ordem oficial
 * Remove colunas técnicas e retorna labels humanas
 * Extrai de normalizedData se disponível
 * Respeita a ordem definida em COLUMN_ORDER
 */
export function getDisplayColumns(data: Record<string, any>[]): string[] {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const columnsSet = new Set<string>();

  for (const row of data) {
    if (!row || typeof row !== 'object') {
      continue;
    }

    const sourceData = getSourceData(row);

    for (const key of Object.keys(sourceData)) {
      if (!HIDDEN_FIELDS.has(key)) {
        const label = FIELD_LABELS[key.toLowerCase()] || key;
        columnsSet.add(label);
      }
    }
  }

  const orderedColumns: string[] = [];
  for (const column of COLUMN_ORDER) {
    if (columnsSet.has(column)) {
      orderedColumns.push(column);
      columnsSet.delete(column);
    }
  }

  for (const column of columnsSet) {
    orderedColumns.push(column);
  }

  return orderedColumns;
}

/**
 * Seleciona as colunas mais úteis para dupla checagem do lote.
 */
export function getImportPreviewColumns(data: Record<string, any>[], maxColumns = 6): string[] {
  const availableColumns = getDisplayColumns(data).filter((column) => !PREVIEW_EXCLUDED_COLUMNS.has(column));

  if (availableColumns.length === 0) {
    return [];
  }

  const preferredColumns: string[] = [];

  for (const column of PREVIEW_PRIORITY) {
    if (availableColumns.includes(column) && !preferredColumns.includes(column)) {
      preferredColumns.push(column);
    }

    if (preferredColumns.length >= maxColumns) {
      return preferredColumns;
    }
  }

  for (const column of availableColumns) {
    if (!preferredColumns.includes(column)) {
      preferredColumns.push(column);
    }

    if (preferredColumns.length >= maxColumns) {
      break;
    }
  }

  return preferredColumns;
}

/**
 * Extrai resumo de verificação de duplicidade (Setor, Cargo, Qtd)
 * Usado para exibir informações essenciais em cards de NEW/SKIPPED/CONFLICTED
 */
export function getIdempotencySummary(data: Record<string, any>): {
  setor: string;
  cargo: string;
  quantidade: string;
} {
  if (!data || typeof data !== 'object') {
    return { setor: EMPTY_VALUE, cargo: EMPTY_VALUE, quantidade: EMPTY_VALUE };
  }

  const sources = getCandidateSources(data);
  const setorKeys = ['setor', 'sector', 'departamento', 'department', 'area'];
  const cargoKeys = ['cargo', 'position', 'job_title', 'jobTitle', 'funcao', 'role'];
  const qtdKeys = [
    'quantidade',
    'quantidade_de_colaboradores',
    'quantity',
    'qtd',
    'numero_pessoas',
    'number_of_people',
    'count',
    'headcount',
  ];

  const setor = getFieldValueFromSources(sources, setorKeys);
  const cargo = getFieldValueFromSources(sources, cargoKeys);
  const quantidade = getFieldValueFromSources(sources, qtdKeys);

  return {
    setor: formatFieldValue(setor),
    cargo: formatFieldValue(cargo),
    quantidade: formatFieldValue(quantidade),
  };
}

/**
 * Extrai dados de uma linha para exibição
 * Mapeia campos técnicos para labels e filtra dados relevantes
 * Extrai de normalizedData se disponível
 * Garante que campos faltantes apareçam como "—"
 */
export function extractDisplayRow(
  row: Record<string, any>,
  columns: string[]
): Record<string, string> {
  const displayRow: Record<string, string> = {};
  const sourceData = getSourceData(row);

  for (const column of columns) {
    const originalKey = Object.entries(FIELD_LABELS).find(([, label]) => label === column)?.[0];

    let value = EMPTY_VALUE;

    if (originalKey) {
      const rawValue = getRawFieldValue(sourceData, [originalKey]);
      if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
        value = formatFieldValue(rawValue);
      }
    }

    if (value === EMPTY_VALUE) {
      const key = Object.keys(sourceData).find(
        (entryKey) =>
          entryKey.toLowerCase() === column.toLowerCase() ||
          FIELD_LABELS[entryKey.toLowerCase()] === column
      );

      if (key && !HIDDEN_FIELDS.has(key)) {
        value = formatFieldValue(sourceData[key]);
      }
    }

    displayRow[column] = value;
  }

  return displayRow;
}

function normalizeStatusKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Resolve o número da linha original para a UI de conferência.
 */
export function getImportRowLineNumber(row: Record<string, any>, fallbackRowNumber?: number): string {
  const lineValue = getFieldValueFromSources(getCandidateSources(row), LINE_NUMBER_KEYS);

  if (lineValue !== null && lineValue !== undefined && lineValue !== '') {
    return formatFieldValue(lineValue);
  }

  if (fallbackRowNumber !== undefined) {
    return String(fallbackRowNumber);
  }

  return EMPTY_VALUE;
}

/**
 * Resolve um status legível da linha para a etapa 5.
 */
export function getImportRowStatus(row: Record<string, any>): string | null {
  const sources = getCandidateSources(row);
  const rawStatus = getFieldValueFromSources(sources, ROW_STATUS_KEYS);

  if (rawStatus === null || rawStatus === undefined || rawStatus === '') {
    return null;
  }

  const normalizedStatus = normalizeStatusKey(String(rawStatus));

  const knownStatuses: Record<string, string> = {
    new: 'Novo',
    skipped: 'Duplicada / ignorada',
    conflicted: 'Conflito detectado',
    valid: 'Válida no lote',
    validated: 'Validada',
    approved: 'Aprovada',
    imported: 'Importada',
    processed: 'Processada',
    processing: 'Em processamento',
    confirmed: 'Confirmada',
    with_errors: 'Com erro',
    error: 'Com erro',
    rejected: 'Rejeitada',
    blocked: 'Bloqueada',
    draft: 'Rascunho',
  };

  if (knownStatuses[normalizedStatus]) {
    return knownStatuses[normalizedStatus];
  }

  const formatted = String(rawStatus)
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/(^\w|\s\w)/g, (match) => match.toUpperCase());

  return formatted || null;
}
